import type { Event as AnalyticsEvent } from "@amplitude/analytics-types";
import { OctopusRoutingProvider } from "@octopusdeploy/design-system-components";
import { logger } from "@octopusdeploy/logging";
import { OctopusClientProvider } from "@octopusdeploy/octopus-react-client";
import type { UserResource } from "@octopusdeploy/octopus-server-client";
import { UserPermissions, OctopusError, AuthenticationError } from "@octopusdeploy/octopus-server-client";
import type { AnalyticSession } from "@octopusdeploy/portal-analytics";
import { useAnalyticSession, AnalyticSessionProvider } from "@octopusdeploy/portal-analytics";
import { OctopusSessionProvider } from "@octopusdeploy/session";
import * as React from "react";
import { useState } from "react";
import { Route, Routes } from "react-router-dom";
import { AdoraIntegration } from "~/analytics/AdoraIntegration";
import { AmplitudeSession } from "~/analytics/AmplitudeSession";
import { useAnalyticExternalLinkDispatch } from "~/analytics/Analytics";
import AuthenticationLayout from "~/areas/authentication/AuthenticationLayout";
import Register from "~/areas/authentication/Register";
import SignIn from "~/areas/authentication/SignIn";
import SignOut from "~/areas/authentication/SignOut";
import { client, repository, session } from "~/clientInstance";
import { AuthenticatedRoutes } from "~/components/AuthenticationRoutes/AuthenticatedRoutes";
import { DevDrawerContent } from "~/components/DevTools/DevDrawerContent";
import { LayoutWithDevTools } from "~/components/DevTools/LayoutWithDevTools";
import type { OverrideoverrideFontProps } from "~/components/DevTools/OverrideFontTab/OverrideFontTab";
import ReloadableRouteContent from "~/components/ReloadableRoute";
import SecureRoute from "~/components/SecureRoute";
import { useThemePaletteType } from "~/components/Theme/useThemePaletteType";
import { InAppMessaging } from "~/inAppMessaging/InAppMessaging";
import { ActiveWorkflowsProvider } from "~/workflows/ActiveWorkflowsProvider";
import { allWorkflowRegistrations } from "~/workflows/workflowRegistrations";
import { useIsPortalUrlActive } from "../Navigation/useIsPortalUrlActive";
import { PortalDesignSystemLink } from "./PortalDesignSystemLink";
import { authRoutes } from "./authRoutes";
type SessionStartEvent = (user: UserResource) => Promise<void>;
interface AuthenticationRoutesProps {
    overrideFontProps: OverrideoverrideFontProps;
}
export function AuthenticationRoutes({ overrideFontProps, ...props }: AuthenticationRoutesProps) {
    const fallbackAnalyticSession = useAnalyticSession();
    const theme = useThemePaletteType();
    const [analyticsSession, setAnalyticsSession] = useState<AnalyticSession | undefined>(undefined);
    const [analyticsEvents, setAnalyticsEvents] = useState<AnalyticsEvent[]>([]);
    const [inAppMessagingSession, setInAppMessagingSession] = useState<InAppMessaging | undefined>(undefined);
    const [adoraSession, setAdoraSession] = useState<AdoraIntegration | undefined>(undefined);
    const onSessionStart: SessionStartEvent = async (user) => {
        try {
            const [featureConfigurations, featureToggles, installationHistory, gitHubAppSettings] = await Promise.all([
                repository.FeaturesConfiguration.get(),
                repository.ServerConfiguration.configuredFeatureToggles(),
                getInstallationHistory(),
                repository.GitHubApp.getSettings(),
            ]);
            await session.start(user, featureConfigurations, featureToggles, installationHistory, gitHubAppSettings);
        }
        catch (error) {
            throw getSessionStartAuthenticationError(error);
        }
        if (analyticsSession === undefined) {
            const serverInfo = client.tryGetServerInformation();
            const telemetryConfig = await repository.TelemetryConfiguration.get();
            if (serverInfo) {
                const session = await AmplitudeSession.create(serverInfo.installationId, user.Id, telemetryConfig.Enabled, theme, (event) => setAnalyticsEvents((prev) => [...prev, event]));
                setAnalyticsSession(session);
                // session.track("Sign In", {});
            }
        }
        try {
            const experiencesConfiguration = await repository.ExperiencesConfiguration.get();
            if (inAppMessagingSession === undefined && experiencesConfiguration.IsEnabled) {
                const session = await InAppMessaging.create();
                setInAppMessagingSession(session);
            }
        }
        catch (error) {
            // We do not want failure related to in-app message interrupting session startup,
            // so this catch is a noop besides logging.
            logger.warn("Failed to start in-app messaging: {error}", { error });
        }
        try {
            if (adoraSession === undefined) {
                const session = await AdoraIntegration.create();
                setAdoraSession(session);
            }
        }
        catch (error) {
            // We do not want failure related to Adora integration interrupting session startup,
            // so this catch is a noop besides logging.
            logger.warn("Failed to start Adora integration: {error}", { error });
        }
    };
    const onSessionEnd = () => {
        analyticsSession?.track("Sign Out", {});
        analyticsSession?.end();
        setAnalyticsSession(undefined);
        inAppMessagingSession?.clear();
        setInAppMessagingSession(undefined);
        adoraSession?.clear();
        setAdoraSession(undefined);
    };
    async function getInstallationHistory() {
        try {
            return await repository.InstallationHistory.getInstallationHistory();
        }
        catch (error) {
            // Failing to fetch InstallationHistory should not prevent login - so this is performed pre-emptively.
            // Will result in the 'Release Notes' linking to an error page.
            logger.error("Failed to fetch InstallationHistory");
        }
        return [];
    }
    const palette = useThemePaletteType();
    const userPermissions = useCurrentUserPermissions();
    return (<OctopusRoutingProvider Link={PortalDesignSystemLink} useIsUrlActive={useIsPortalUrlActive} useDispatchLinkAnalytics={useAnalyticExternalLinkDispatch}>
            <OctopusSessionProvider currentUser={session.currentUser} currentPermissions={userPermissions}>
                <OctopusClientProvider client={client}>
                    <AnalyticSessionProvider session={analyticsSession ?? fallbackAnalyticSession}>
                        <ActiveWorkflowsProvider workflowRegistrations={allWorkflowRegistrations}>
                            <LayoutWithDevTools devToolsContent={<DevDrawerContent analyticsEvents={analyticsEvents} overrideFontProps={overrideFontProps}/>}>
                                <Routes>
                                    <Route path={authRoutes.signIn} element={<ReloadableRouteContent>
                                                <AuthenticationLayout>
                                                    <SignIn onSessionStarted={onSessionStart}/>
                                                </AuthenticationLayout>
                                            </ReloadableRouteContent>}/>
                                    <Route path={authRoutes.signOut} element={<ReloadableRouteContent>
                                                <AuthenticationLayout>
                                                    <SignOut onSessionEnded={onSessionEnd}/>
                                                </AuthenticationLayout>
                                            </ReloadableRouteContent>}/>
                                    <Route path={authRoutes.register(":inviteCode")} element={<ReloadableRouteContent>
                                                <AuthenticationLayout>
                                                    <Register onSessionStarted={onSessionStart}/>
                                                </AuthenticationLayout>
                                            </ReloadableRouteContent>}/>
                                    <Route path="*" element={<SecureRoute analyticsSession={analyticsSession}>
                                                <AuthenticatedRoutes />
                                            </SecureRoute>}/>
                                </Routes>
                            </LayoutWithDevTools>
                        </ActiveWorkflowsProvider>
                    </AnalyticSessionProvider>
                </OctopusClientProvider>
            </OctopusSessionProvider>
        </OctopusRoutingProvider>);
}
function getSessionStartAuthenticationError(error: unknown): AuthenticationError {
    let reason = "There was an unknown error";
    if (error instanceof OctopusError) {
        reason = error.StatusCode === 401 ? "This can happen if the Octopus authentication cookie is blocked." : `There was a problem communicating with the Octopus Server: ${error.ErrorMessage}`;
    }
    else if (error instanceof Error) {
        reason = `There was an unknown error: ${error.message}`;
    }
    const message = "The sign in succeeded but we failed to get the resultant permissions for this user account. ";
    return new AuthenticationError(message + reason);
}
function useCurrentUserPermissions() {
    const [userPermissions, setUserPermissions] = useState(() => session.currentPermissions ?? UserPermissions.None);
    React.useEffect(() => session.subscribe(() => {
        setUserPermissions(session.currentPermissions ?? UserPermissions.None);
    }), []);
    return userPermissions;
}
